<script setup lang="ts">
import { computed, watch, ref } from 'vue';
import { useForm } from 'vee-validate';
import { useAsyncState } from '@vueuse/core';

import api from '@/api';
import { defaultAdditionalFundRequired } from '@/helpers/builder';
import {
  LoanPeriod,
  loanPeriodOptions,
  LoanType,
  AdditionalFundsPurpose,
  AdditionalFundsPurposeOptions,
  CommercialAdditionalFundsPurposeOptions,
} from '@/helpers/const';
import dayjs from '@/helpers/dayjs';
import { formatCurrency, snakeCase } from '@/helpers/format';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { DraftEventName } from '@/helpers/draftEvents';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { useCalcApi } from '@/helpers/useCalcApi';
import { useProductType } from '@/helpers/useProductType';
import { brokerLoanDetailsSchema } from '@/validation';

import type {
  AdditionalFundsPurposeEnum,
  AdditionalFundsRequired,
  AdditionalFundsRequiredEntry,
  LoanDetails,
  LoanDetailsEntry,
} from '@/types';

const DEFAULT_BROKER_FEE = 0.25;
const isLoading = ref(false);

const { state, loanApp, saveAndGoNext, goBack } = useBrokerLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();

type LoanDetailsEntryWithAdditionalRequiredFundsEntry = LoanDetailsEntry & {
  additionalFundsRequiredEntry: AdditionalFundsRequiredEntry;
};

const {
  meta,
  submitCount,
  values,
  handleSubmit: validateSubmit,
  resetForm,
} = useForm<LoanDetailsEntryWithAdditionalRequiredFundsEntry>({
  validationSchema: brokerLoanDetailsSchema,
  initialValues: { ...loanApp.value.loanDetails, additionalFundsRequiredEntry: loanApp.value.additionalFundsRequired },
  validateOnMount: false,
});

resetForm({
  values: {
    ...loanApp.value.loanDetails,
    additionalFundsRequiredEntry: loanApp.value.additionalFundsRequired,
    brokerFeePercentage: loanApp.value.loanDetails?.brokerFeePercentage ?? DEFAULT_BROKER_FEE,
    loanType: loanApp.value.loanType ?? LoanType.BRIDGING,
  },
});

const calc = useCalcApi();

const { state: loanAmount, isReady } = useAsyncState(
  calc.getLoanAmount({ ...loanApp.value, loanDetails: undefined }),
  0,
);

const calcLoanAmount = computed(() => {
  const { savingsAmount = 0, giftedAmount = 0 } = values;
  return giftedAmount <= savingsAmount ? loanAmount.value - savingsAmount + giftedAmount : 'invalid amount';
});

/** Base minimum after contributions; plus any additional borrowing purposes for broker display and fee. */
const minLoanAmountRequired = computed(() => {
  const base = calcLoanAmount.value;
  if (typeof base !== 'number') {
    return base;
  }
  const additional =
    values.additionalFundsRequiredEntry?.isAdditionalFundsRequired === true
      ? values.additionalFundsRequiredEntry.fundsRequiredAmount ?? 0
      : 0;
  return base + additional;
});

const brokerFeeAmount = computed(() => {
  if (loanApp.value.userType !== 'Broker' || !values.brokerFeePercentage) {
    return 0;
  }
  const totalLoanAmount = typeof minLoanAmountRequired.value === 'number' ? minLoanAmountRequired.value : 0;
  return Math.round((totalLoanAmount * values.brokerFeePercentage) / 100);
});

const isBroker = computed(() => loanApp.value.userType === 'Broker');
const isBridgingLoan = computed(() => loanApp.value.loanType === LoanType.BRIDGING);

const { isCommercialProduct } = useProductType(loanApp);

const fundsPurposeOptions = computed(() => {
  const { [AdditionalFundsPurpose.OTHER]: otherOption, ...baseOptions } = AdditionalFundsPurposeOptions;

  if (!isCommercialProduct.value) {
    return { ...baseOptions, [AdditionalFundsPurpose.OTHER]: otherOption };
  }

  return {
    ...baseOptions,
    ...CommercialAdditionalFundsPurposeOptions,
    [AdditionalFundsPurpose.OTHER]: otherOption,
  };
});

const fundsDescriptionPrompt = computed(() => {
  const prompts: Record<string, string> = {
    [AdditionalFundsPurpose.FINANCIAL_CONSOLIDATION]: 'Explain the financial consolidation requirements',
    [AdditionalFundsPurpose.COSMETIC_IMPROVEMENTS_BEFORE_SELLING]: 'Explain the cosmetic improvements being done',
    [AdditionalFundsPurpose.OTHER]: 'Provide the details of the additional borrowing purposes',
    [AdditionalFundsPurpose.BUSINESS_PURPOSE]: 'Explain the business purpose requirements',
    [AdditionalFundsPurpose.COMPLETE_PROJECT_FINANCE]: 'Explain the project finance requirements',
  };
  return prompts[values.additionalFundsRequiredEntry?.reasonForFundsOption ?? ''] ?? '';
});

const handleSubmit = validateSubmit(async (values) => {
  isLoading.value = true;
  const additionalFundsPurposeId = listAdditionalFundsPurposes.value?.find(
    (item) => snakeCase(item.title) == values.additionalFundsRequiredEntry.reasonForFundsOption,
  )?.id;

  const additionalFundsRequired: AdditionalFundsRequired = Object.assign(defaultAdditionalFundRequired(), {
    isAdditionalFundsRequired: values.additionalFundsRequiredEntry.isAdditionalFundsRequired,
    description: values.additionalFundsRequiredEntry.description ?? '',
    fundsRequiredDate: values.additionalFundsRequiredEntry.fundsRequiredDate,
    fundsRequiredAmount: values.additionalFundsRequiredEntry.fundsRequiredAmount ?? 0,
    reasonForFundsOption: values.additionalFundsRequiredEntry.reasonForFundsOption,
    additionalFundsPurposeId: additionalFundsPurposeId,
  });

  const loanDetails: LoanDetails = {
    savingsAmount: values.savingsAmount ?? 0,
    giftedAmount: values.giftedAmount ?? 0,
    reasonDescription: values.reasonDescription ?? '',
    brokerFeePercentage: values.brokerFeePercentage ?? 0,
    borrowAmount: values.borrowAmount ?? 0,
    loanPeriod: values.loanPeriod,
    loanDate: values.loanDate,
    calcLoanAmount: loanAmount.value,
  };

  if (isBridgingLoan.value) {
    loanDetails.loanPeriod = undefined;
    loanDetails.loanDate = undefined;
  } else {
    if (loanDetails.loanPeriod !== LoanPeriod.EXACT_DATE) {
      loanDetails.loanDate = dayjs().add(Number(loanDetails.loanPeriod), 'month').format('YYYY-MM-DD');
    }
  }
  loanDetails.calcLoanAmount = await calc.getLoanAmount(loanApp.value);

  let applicationDraftId = loanApp.value.applicationDraftId;
  try {
    if (loanApp.value.brokerId) {
      applicationDraftId = await saveApplicationToDraft(
        state.value.context,
        {
          ...loanApp.value,
          loanDetails,
          additionalFundsRequired,
        },
        DraftEventName.LoanDetails,
      );
    }
    saveAndGoNext({ loanDetails, additionalFundsRequired, applicationDraftId });
  } catch (error) {
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
});

const { state: listAdditionalFundsPurposes, isReady: isListReady } = useAsyncState(
  () => api.backend.public.listAdditionalFundsPurposes(),
  [],
);

// Sync backend additionalFundsPurposeId (UUID) to form's reasonForFundsOption (snake_case enum)
// This runs when the API list is ready and backend data is available
watch(
  () => [
    listAdditionalFundsPurposes.value?.length,
    loanApp.value.additionalFundsRequired?.additionalFundsPurposeId,
    isListReady.value,
  ],
  () => {
    const formValue = values.additionalFundsRequiredEntry?.reasonForFundsOption;
    const backendPurposeId = loanApp.value.additionalFundsRequired?.additionalFundsPurposeId;
    if (!listAdditionalFundsPurposes.value?.length || !backendPurposeId) {
      return;
    }

    const matchingOption = listAdditionalFundsPurposes.value.find((item) => item.id === backendPurposeId);
    if (!matchingOption?.title) {
      return;
    }

    const backendValue = snakeCase(matchingOption.title) as AdditionalFundsPurposeEnum;

    // Only sync if form doesn't have a value yet (initial load)
    if (!formValue) {
      resetForm({
        values: {
          ...values,
          additionalFundsRequiredEntry: {
            ...values.additionalFundsRequiredEntry,
            reasonForFundsOption: backendValue,
          },
        },
      });
    }
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">
        <template v-if="isBridgingLoan">
          Will your client make <BxHighlight>additional financial contributions</BxHighlight> to the purchase of the
          property?
        </template>
        <template v-else> How much do you want to <BxHighlight>borrow</BxHighlight>? </template>
      </h2>
    </div>

    <form @submit.prevent="handleSubmit">
      <div v-if="isBridgingLoan">
        <BxField v-slot="{ field }" name="savingsAmount">
          <BxNumberInput
            v-bind="field"
            id="additional-funds-amount"
            label="Additional contributions amount"
            label-type="floating"
            leading-symbol="$"
            :allow-negative="false"
            class="form-control"
            help-text="leave blank if there are no additional contributions to be made."
          />
        </BxField>

        <p class="mt-2 text-xs">
          * Do not include the deposit to be paid on your new property or the proceeds from the sale of their existing
          property
        </p>

        <template v-if="values.savingsAmount">
          <h3 class="text-md mb-4 mt-6 font-medium">How much of these funds have been gifted to you?</h3>
          <BxField v-slot="{ field }" name="giftedAmount">
            <BxNumberInput
              v-bind="field"
              id="additional-funds-gifted-amount"
              label="Gifted amount"
              label-type="floating"
              leading-symbol="$"
              :allow-negative="false"
              class="form-control"
            />
          </BxField>
        </template>
      </div>

      <div v-if="isBroker && isBridgingLoan" class="my-8">
        <p class="font-medium">The minimum loan amount required to complete the purchase is:</p>
        <p v-if="typeof minLoanAmountRequired === 'number'" class="my-2 text-2xl font-medium text-bridgit-green">
          <template v-if="isReady">{{ formatCurrency(minLoanAmountRequired) }}</template>
          <BxSpinner v-else />
        </p>
        <p v-else class="my-2 text-2xl font-medium text-rose-500">Invalid amount</p>
      </div>

      <template v-if="isBroker || !isBridgingLoan">
        <h3 class="text-md mb-1 mt-10 font-medium">
          {{
            isBridgingLoan
              ? 'Are there any additional funds required for other borrowing purposes?'
              : 'How much do you want to borrow?'
          }}
        </h3>

        <BxField v-slot="{ field }" name="additionalFundsRequiredEntry.isAdditionalFundsRequired">
          <BxRadio
            id="additional-funds-required"
            v-bind="field"
            label=""
            label-type="default"
            class="form-control"
            boolean
          />
        </BxField>
        <BxField
          v-if="values.additionalFundsRequiredEntry.isAdditionalFundsRequired"
          v-slot="{ field }"
          name="additionalFundsRequiredEntry.reasonForFundsOption"
        >
          <BxSelect
            v-bind="field"
            label="Purpose for the additional funds"
            label-type="floating"
            placeholder="Purpose for the additional funds"
            :list-items="fundsPurposeOptions"
            :truncate="false"
            class="form-control"
          />
        </BxField>
        <BxField
          v-if="
            values?.additionalFundsRequiredEntry?.isAdditionalFundsRequired &&
            values.additionalFundsRequiredEntry.reasonForFundsOption
          "
          v-slot="{ field }"
          name="additionalFundsRequiredEntry.fundsRequiredAmount"
        >
          <BxNumberInput
            v-bind="field"
            label="Funds required"
            :max="999999999"
            label-type="floating"
            leading-symbol="$"
            :allow-negative="false"
            class="form-control"
          />
        </BxField>
        <BxField
          v-if="
            values?.additionalFundsRequiredEntry?.isAdditionalFundsRequired &&
            values.additionalFundsRequiredEntry.reasonForFundsOption
          "
          v-slot="{ field }"
          name="additionalFundsRequiredEntry.description"
        >
          <p v-if="fundsDescriptionPrompt" class="mb-2 mt-4">
            {{ fundsDescriptionPrompt }}
          </p>
          <BxTextarea v-bind="field" class="form-control" />
        </BxField>
      </template>

      <div v-if="isBroker && false" class="form-control flex items-center gap-8">
        <BxField v-slot="{ field }" name="brokerFeePercentage">
          <BxNumberInput
            v-bind="field"
            label="Broker fee"
            label-type="floating"
            placeholder="0.00"
            :precision="2"
            :allow-negative="false"
            class="form-control w-40"
          >
            <template #trailing><div class="mx-3">%</div></template>
          </BxNumberInput>
        </BxField>
        <div class="relative w-40 rounded border border-solid border-gray-300 px-3 py-2">
          <label
            class="absolute -top-2 left-2 -mt-px inline-block bg-bridgit-paleBlue px-1 text-xs font-medium text-gray-900"
            >Broker fee amount</label
          >
          <div class="">{{ formatCurrency(brokerFeeAmount) }}</div>
        </div>
      </div>

      <template v-if="!isBridgingLoan">
        <BxField v-slot="{ field }" name="loanPeriod">
          <BxRadio
            class="form-control"
            v-bind="field"
            :options="loanPeriodOptions"
            label="When do you need the funds?"
            label-type="default"
            block
          />
        </BxField>
        <BxField v-if="values.loanPeriod == LoanPeriod.EXACT_DATE" v-slot="{ field }" name="loanDate">
          <BxDatePicker v-bind="field" class="form-control" label="" placeholder="DD/MM/YYYY" />
        </BxField>
      </template>

      <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
        Please review the error messages above.
      </BxErrorPanel>
      <div class="mt-10">
        <BxButton id="loan-details-continue" variant="primary" :loading="!isListReady || isLoading" type="submit"
          >Save and continue</BxButton
        >
        <BxButton v-if="state.nextEvents.includes('BACK')" variant="link" class="ml-4" @click="goBack()">Back</BxButton>
      </div>
    </form>
  </div>
</template>
